'use client';
import React, { useEffect, useState, useMemo } from 'react';
import { DirectoryProvider } from '@/components/directory/DirectoryContext';
import { useDynamicAirtableRecords } from '@/lib/airtable/client-hooks';
import { parseAttachments } from '@/lib/airtable/transform';
import Link from 'next/link';
import { FolderOpen } from 'lucide-react';
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@/components/ui/select';
import MultiSelect from '@/components/ui/multi-select';
import { Pill } from '@/components/ui/Pill';
import { LoadingPattern } from "@/components/ui/pattern-viewer";
import PlatformDirectorySection from './PlatformDirectorySection';
import PlatformFilterBar from './PlatformFilterBar';
import PlatformActiveFilters from './PlatformActiveFilters';
import { Breadcrumb } from '@/components/ui/breadcrumb';

function getDisplayValue(obj: any): string {
  if (!obj || typeof obj !== 'object') return '';
  if (obj.Name) return obj.Name;
  if (obj.name) return obj.name;
  const firstString = Object.values(obj).find(v => typeof v === 'string');
  if (firstString) return String(firstString);
  if (obj.id) return obj.id;
  return '[object]';
}

export default function PlatformsPage() {
  // Fetch all Platforms
  const { data, isLoading, isError } = useDynamicAirtableRecords('PLATFORMS');
  // Fetch schema for dynamic field detection
  const [schema, setSchema] = useState<any>(null);
  const [schemaLoading, setSchemaLoading] = useState(true);

  useEffect(() => {
    setSchemaLoading(true);
    fetch('/api/airtable/schema')
      .then(res => res.json())
      .then(data => setSchema(data))
      .catch(() => setSchema(null))
      .finally(() => setSchemaLoading(false));
  }, []);

  // Find the schema for the current table
  const currentTableSchema = useMemo(
    () => schema?.tables?.find((t: any) => t.name === 'Platforms' || t.id === 'PLATFORMS'),
    [schema]
  );

  // Helper to extract fields for a Platform record
  function extractFields(platform: any) {
    if (!platform || !currentTableSchema) return {};
    // Use schema's primaryFieldId for the name
    const primaryFieldId = currentTableSchema.primaryFieldId;
    const primaryFieldName = currentTableSchema.fields.find((f: any) => f.id === primaryFieldId)?.name || 'Platform Name';
    // Find description field
    const descriptionField = currentTableSchema.fields.find((f: any) => f.name.toLowerCase().includes('description') || f.name.toLowerCase().includes('summary'))?.name;
    // Find website field
    const fieldsToShow = Object.keys(platform);
    const websiteField = fieldsToShow.find(f => f.toLowerCase().includes('website'));
    // Find location field
    const locationField = fieldsToShow.find(f => f.toLowerCase() === 'location');
    // Slug field
    const slugField = fieldsToShow.find(f => f.toLowerCase() === 'slug');

    // Extract values
    const attachments = (platform['Logo'] && Array.isArray(platform['Logo']))
      ? parseAttachments(platform['Logo']) ?? []
      : [];
    const logo = attachments.length > 0 ? attachments[0] : null;
    const logoSrc = logo && typeof logo === 'object' ? (logo.thumbnailLarge ?? logo.url ?? '') : '';
    
    // Extract Provider Logo from lookup field
    const providerLogoSrc = (() => {
      const providerLogoField = platform['Provider Logo'];
      if (!providerLogoField) return '';
      
      // Handle lookup field structure - it contains attachment data from linked provider records
      if (Array.isArray(providerLogoField)) {
        // Find the first provider logo attachment
        for (const logoData of providerLogoField) {
          if (logoData && typeof logoData === 'object') {
            // The lookup field contains attachment objects directly
            if (logoData.thumbnailLarge) return logoData.thumbnailLarge;
            if (logoData.url) return logoData.url;
          }
        }
      }
      
      return '';
    })();
    
    // Extract image with fallback system: Instrument Image → Assay Kit Image → Assay Panel Image → Software Image
    const getImageSrc = (fieldName: string) => {
      const attachments = (platform[fieldName] && Array.isArray(platform[fieldName]))
        ? parseAttachments(platform[fieldName]) ?? []
        : [];
      const image = attachments.length > 0 ? attachments[0] : null;
      return image && typeof image === 'object' ? (image.thumbnailLarge ?? image.url ?? '') : '';
    };

    const instrumentImageSrc = getImageSrc('Instrument Image') || 
                              getImageSrc('Assay Kit Image') || 
                              getImageSrc('Assay Panel Image') || 
                              getImageSrc('Software Image') || '';
    
    const name = platform[primaryFieldName] || '';
    const description = descriptionField ? platform[descriptionField] : '';
    const website = websiteField ? platform[websiteField] : undefined;
    let websiteUrl = website;
    if (website && typeof website === 'string' && !/^https?:\/\//i.test(website)) websiteUrl = 'https://' + website;
    let location = locationField ? platform[locationField] : undefined;
    if (!location) {
      const city = platform['City'] || '';
      const state = platform['State'] || '';
      const country = platform['Country'] || '';
      location = [city, state, country].filter(Boolean).join(', ');
    }
    const slug = slugField ? platform[slugField] : undefined;
    return { logoSrc, providerLogoSrc, instrumentImageSrc, name, description, website, websiteUrl, location, slug };
  }

  // Sort Platforms alphabetically by name
  const sortedData = Array.isArray(data) ? [...data].sort((a, b) => {
    const nameA = (extractFields(a).name || '').toLowerCase();
    const nameB = (extractFields(b).name || '').toLowerCase();
    return nameA.localeCompare(nameB);
  }) : [];



  // Add state for filters
  const [selectedCountries, setSelectedCountries] = useState<string[]>([]);
  const [selectedProviders, setSelectedProviders] = useState<string[]>([]);
  const [selectedTechnologies, setSelectedTechnologies] = useState<string[]>([]);
  const [selectedProducts, setSelectedProducts] = useState<string[]>([]);
  const [selectedReadouts, setSelectedReadouts] = useState<string[]>([]);
  const [selectedPlexTypes, setSelectedPlexTypes] = useState<string[]>([]);
  const [selectedChemistry, setSelectedChemistry] = useState<string[]>([]);
  const [selectedResolution, setSelectedResolution] = useState<string[]>([]);
  const [selectedThroughput, setSelectedThroughput] = useState<string[]>([]);
  // 1. Add state for selectedSamples
  const [selectedSamples, setSelectedSamples] = useState<string[]>([]);
  // Add state for new filters
  const [selectedSlideResolution, setSelectedSlideResolution] = useState<string[]>([]);
  const [selectedApplication, setSelectedApplication] = useState<string[]>([]);

  // Fetch all Technology records for the filter dropdown
  const { data: technologyRecords, isLoading: techLoading } = useDynamicAirtableRecords('TECHNOLOGY');
  const technologyLookup = useMemo(() => {
    if (!technologyRecords || !Array.isArray(technologyRecords)) return {};
    const map: Record<string, string> = {};
    technologyRecords.forEach((rec: any) => {
      const name = rec['Technology Name'] || rec['Name'] || rec.id;
      map[rec.id] = name;
    });
    return map;
  }, [technologyRecords]);

  // Extract unique technologies from Platforms data using the lookup
  const technologyOptions = useMemo(() => {
    if (!data || !Array.isArray(data) || !technologyLookup) return [];
    const technologies = new Set<string>();
    data.forEach((platform: any) => {
      const technologyField = platform['Technology'];
      if (Array.isArray(technologyField)) {
        technologyField.forEach((tech) => { 
          if (tech) {
            // Handle both string values and object references
            const techId = typeof tech === 'string' ? tech : (tech.id || tech);
            const techName = technologyLookup[techId] || techId;
            if (techName) technologies.add(techName);
          }
        });
      } else if (technologyField) {
        // Handle single value
        const techId = typeof technologyField === 'string' ? technologyField : (technologyField.id || technologyField);
        const techName = technologyLookup[techId] || techId;
        if (techName) technologies.add(techName);
      }
    });
    return Array.from(technologies).sort((a, b) => a.localeCompare(b));
  }, [data, technologyLookup]);

  // Fetch all Products records for the filter dropdown
  const { data: productRecords, isLoading: productLoading } = useDynamicAirtableRecords('PRODUCTS');
  const productOptions = useMemo(() => {
    if (!productRecords || !Array.isArray(productRecords)) return [];
    return productRecords
      .map((rec: any) => ({ id: rec.id, name: rec['Name'] || rec['Product Name'] || rec.id }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [productRecords]);

  // Fetch all Providers records for the filter dropdown
  const { data: providerRecords, isLoading: providerLoading } = useDynamicAirtableRecords('PROVIDERS');
  const providerLookup = useMemo(() => {
    if (!providerRecords || !Array.isArray(providerRecords)) return {};
    const map: Record<string, string> = {};
    providerRecords.forEach((rec: any) => {
      const name = rec['Company'] || rec['Provider'] || rec['Name'] || rec.id;
      map[rec.id] = name;
    });
    return map;
  }, [providerRecords]);

  // Extract unique countries from Platforms data
  const countryOptions = useMemo(() => {
    if (!data || !Array.isArray(data)) return [];
    const countries = new Set<string>();
    data.forEach((platform: any) => {
      const countryField = platform['Country'];
      if (Array.isArray(countryField)) {
        countryField.forEach((c) => { if (c) countries.add(c); });
      } else if (typeof countryField === 'string' && countryField) {
        countries.add(countryField);
      }
    });
    return Array.from(countries).sort((a, b) => a.localeCompare(b));
  }, [data]);

  // Extract unique providers from Platforms data
  const providerOptions = useMemo(() => {
    if (!data || !Array.isArray(data) || !providerLookup) return [];
    const providers = new Set<string>();
    data.forEach((platform: any) => {
      const providerField = platform['Provider'];
      if (Array.isArray(providerField)) {
        providerField.forEach((p) => { 
          if (p) {
            const providerName = providerLookup[p] || p;
            providers.add(providerName);
          }
        });
      } else if (typeof providerField === 'string' && providerField) {
        const providerName = providerLookup[providerField] || providerField;
        providers.add(providerName);
      }
    });
    return Array.from(providers).sort((a, b) => a.localeCompare(b));
  }, [data, providerLookup]);

  // Extract unique readout options from Platforms data
  const readoutOptions = useMemo(() => {
    if (!data || !Array.isArray(data)) return [];
    const readouts = new Set<string>();
    data.forEach((platform: any) => {
      const readoutField = platform['Readout'];
      if (Array.isArray(readoutField)) {
        readoutField.forEach((r) => { if (r) readouts.add(r); });
      } else if (typeof readoutField === 'string' && readoutField) {
        readouts.add(readoutField);
      }
    });
    return Array.from(readouts).sort((a, b) => a.localeCompare(b));
  }, [data]);

  // Extract unique plex type options from Platforms data
  const plexTypeOptions = useMemo(() => {
    if (!data || !Array.isArray(data)) return [];
    const plexTypes = new Set<string>();
    data.forEach((platform: any) => {
      const plexTypeField = platform['Plex Type'];
      if (Array.isArray(plexTypeField)) {
        plexTypeField.forEach((p) => { if (p) plexTypes.add(p); });
      } else if (typeof plexTypeField === 'string' && plexTypeField) {
        plexTypes.add(plexTypeField);
      }
    });
    return Array.from(plexTypes).sort((a, b) => a.localeCompare(b));
  }, [data]);

  // Extract unique chemistry options from Platforms data
  const chemistryOptions = useMemo(() => {
    if (!data || !Array.isArray(data)) return [];
    const chemistry = new Set<string>();
    data.forEach((platform: any) => {
      const chemistryField = platform['Chemistry'];
      if (Array.isArray(chemistryField)) {
        chemistryField.forEach((c) => { if (c) chemistry.add(c); });
      } else if (typeof chemistryField === 'string' && chemistryField) {
        chemistry.add(chemistryField);
      }
    });
    return Array.from(chemistry).sort((a, b) => a.localeCompare(b));
  }, [data]);

  // Extract unique resolution options from Platforms data
  const resolutionOptions = useMemo(() => {
    if (!data || !Array.isArray(data)) return [];
    const resolution = new Set<string>();
    data.forEach((platform: any) => {
      const resolutionField = platform['Cellular Resolution'];
      if (Array.isArray(resolutionField)) {
        resolutionField.forEach((r) => { if (r) resolution.add(r); });
      } else if (typeof resolutionField === 'string' && resolutionField) {
        resolution.add(resolutionField);
      }
    });
    return Array.from(resolution).sort((a, b) => a.localeCompare(b));
  }, [data]);

  // Extract unique throughput options from Platforms data
  const throughputOptions = useMemo(() => {
    if (!data || !Array.isArray(data)) return [];
    const throughput = new Set<string>();
    data.forEach((platform: any) => {
      const throughputField = platform['Throughput'];
      if (Array.isArray(throughputField)) {
        throughputField.forEach((t) => { if (t) throughput.add(t); });
      } else if (typeof throughputField === 'string' && throughputField) {
        throughput.add(throughputField);
      }
    });
    return Array.from(throughput).sort((a, b) => a.localeCompare(b));
  }, [data]);

  // Extract unique sample types options from Platforms data
  const samplesOptions = useMemo(() => {
    if (!data || !Array.isArray(data)) return [];
    const samples = new Set<string>();
    data.forEach((platform: any) => {
      const samplesField = platform['Sample Types'];
      if (Array.isArray(samplesField)) {
        samplesField.forEach((s) => { if (s) samples.add(s); });
      } else if (typeof samplesField === 'string' && samplesField) {
        samples.add(samplesField);
      }
    });
    return Array.from(samples).sort((a, b) => a.localeCompare(b));
  }, [data]);

  // Extract unique slide resolution options from Platforms data
  const slideResolutionOptions = useMemo(() => {
    if (!data || !Array.isArray(data)) return [];
    const slideResolution = new Set<string>();
    data.forEach((platform: any) => {
      const slideResolutionField = platform['Slide Resolution'];
      if (Array.isArray(slideResolutionField)) {
        slideResolutionField.forEach((sr) => { if (sr) slideResolution.add(sr); });
      } else if (typeof slideResolutionField === 'string' && slideResolutionField) {
        slideResolution.add(slideResolutionField);
      }
    });
    return Array.from(slideResolution).sort((a, b) => a.localeCompare(b));
  }, [data]);

  // Extract unique application options from Platforms data
  const applicationOptions = useMemo(() => {
    if (!data || !Array.isArray(data)) return [];
    const application = new Set<string>();
    data.forEach((platform: any) => {
      const applicationField = platform['Application'];
      if (Array.isArray(applicationField)) {
        applicationField.forEach((app) => { if (app) application.add(app); });
      } else if (typeof applicationField === 'string' && applicationField) {
        application.add(applicationField);
      }
    });
    return Array.from(application).sort((a, b) => a.localeCompare(b));
  }, [data]);

  // Add state for view mode (card or list)
  const [viewMode, setViewMode] = useState<'card' | 'list'>('card');
  
  // Add state for sorting
  const [sortBy, setSortBy] = useState<string>('name-asc');

  // Loading/Error states
  if (isLoading || schemaLoading) {
    return (
      <DirectoryProvider>
        <div className="flex min-h-[60vh] items-center justify-center">
          <LoadingPattern patternName="cell-pattern" size="md" />
        </div>
      </DirectoryProvider>
    );
  }
  if (isError) {
    return (
      <DirectoryProvider>
        <div className="text-center py-20 text-red-500">Error loading Platforms.</div>
      </DirectoryProvider>
    );
  }
  if (!data || !Array.isArray(data) || !currentTableSchema) {
    return (
      <DirectoryProvider>
        <div className="text-center py-20">No Platforms found.</div>
      </DirectoryProvider>
    );
  }

  // Sort and filter platforms
  const filteredAndSortedPlatforms = sortedData.filter((platform: any) => {
    const { name } = extractFields(platform);
    
    // Exclude 'Platform Agnostic'
    if (name === 'Platform Agnostic') return false;
    
    // Country filter logic
    if (selectedCountries.length > 0) {
      const countryField = platform['Country'];
      if (Array.isArray(countryField)) {
        if (!countryField.some((c) => selectedCountries.includes(c))) return false;
      } else if (typeof countryField === 'string') {
        if (!selectedCountries.includes(countryField)) return false;
      } else {
        return false;
      }
    }
    
    // Provider filter logic
    if (selectedProviders.length > 0) {
      const providerField = platform['Provider'];
      if (Array.isArray(providerField)) {
        const platformProviderNames = providerField.map((provider) => {
          const providerId = typeof provider === 'string' ? provider : (provider.id || provider);
          return providerLookup[providerId] || providerId;
        });
        if (!platformProviderNames.some((providerName) => selectedProviders.includes(providerName))) return false;
      } else if (providerField) {
        const providerId = typeof providerField === 'string' ? providerField : (providerField.id || providerField);
        const providerName = providerLookup[providerId] || providerId;
        if (!selectedProviders.includes(providerName)) return false;
      } else {
        return false;
      }
    }
    
    // Technology filter logic
    if (selectedTechnologies.length > 0) {
      const techField = platform['Technology'];
      if (Array.isArray(techField)) {
        const platformTechNames = techField.map((tech) => {
          const techId = typeof tech === 'string' ? tech : (tech.id || tech);
          return technologyLookup[techId] || techId;
        });
        if (!platformTechNames.some((techName) => selectedTechnologies.includes(techName))) return false;
      } else if (techField) {
        const techId = typeof techField === 'string' ? techField : (techField.id || techField);
        const techName = technologyLookup[techId] || techId;
        if (!selectedTechnologies.includes(techName)) return false;
      } else {
        return false;
      }
    }
    
    // Product filter logic
    if (selectedProducts.length > 0) {
      const productField = platform['Products'];
      if (Array.isArray(productField)) {
        if (!productField.some((product) => selectedProducts.includes(product))) return false;
      } else if (typeof productField === 'string') {
        if (!selectedProducts.includes(productField)) return false;
      } else {
        return false;
      }
    }
    
    // Readout filter logic
    if (selectedReadouts.length > 0) {
      const readoutField = platform['Readout'];
      if (Array.isArray(readoutField)) {
        if (!readoutField.some((readout) => selectedReadouts.includes(readout))) return false;
      } else if (typeof readoutField === 'string') {
        if (!selectedReadouts.includes(readoutField)) return false;
      } else {
        return false;
      }
    }
    
    // Plex Type filter logic
    if (selectedPlexTypes.length > 0) {
      const plexTypeField = platform['Plex Type'];
      if (Array.isArray(plexTypeField)) {
        if (!plexTypeField.some((plex) => selectedPlexTypes.includes(plex))) return false;
      } else if (typeof plexTypeField === 'string') {
        if (!selectedPlexTypes.includes(plexTypeField)) return false;
      } else {
        return false;
      }
    }
    
    // Chemistry filter logic
    if (selectedChemistry.length > 0) {
      const chemistryField = platform['Chemistry'];
      if (Array.isArray(chemistryField)) {
        if (!chemistryField.some((chem) => selectedChemistry.includes(chem))) return false;
      } else if (typeof chemistryField === 'string') {
        if (!selectedChemistry.includes(chemistryField)) return false;
      } else {
        return false;
      }
    }
    
    // Resolution filter logic
    if (selectedResolution.length > 0) {
      const resolutionField = platform['Cellular Resolution'];
      if (Array.isArray(resolutionField)) {
        if (!resolutionField.some((res) => selectedResolution.includes(res))) return false;
      } else if (typeof resolutionField === 'string') {
        if (!selectedResolution.includes(resolutionField)) return false;
      } else {
        return false;
      }
    }
    
    // Throughput filter logic
    if (selectedThroughput.length > 0) {
      const throughputField = platform['Throughput'];
      if (Array.isArray(throughputField)) {
        if (!throughputField.some((thru) => selectedThroughput.includes(thru))) return false;
      } else if (typeof throughputField === 'string') {
        if (!selectedThroughput.includes(throughputField)) return false;
      } else {
        return false;
      }
    }
    
    // Sample Types filter logic
    if (selectedSamples.length > 0) {
      const samplesField = platform['Sample Types'];
      if (Array.isArray(samplesField)) {
        if (!samplesField.some((s) => selectedSamples.includes(s))) return false;
      } else if (typeof samplesField === 'string') {
        if (!selectedSamples.includes(samplesField)) return false;
      } else {
        return false;
      }
    }
    
    // Slide Resolution filter logic
    if (selectedSlideResolution.length > 0) {
      const slideResolutionField = platform['Slide Resolution'];
      if (Array.isArray(slideResolutionField)) {
        if (!slideResolutionField.some((sr) => selectedSlideResolution.includes(sr))) return false;
      } else if (typeof slideResolutionField === 'string') {
        if (!selectedSlideResolution.includes(slideResolutionField)) return false;
      } else {
        return false;
      }
    }
    
    // Application filter logic
    if (selectedApplication.length > 0) {
      const applicationField = platform['Application'];
      if (Array.isArray(applicationField)) {
        if (!applicationField.some((app) => selectedApplication.includes(app))) return false;
      } else if (typeof applicationField === 'string') {
        if (!selectedApplication.includes(applicationField)) return false;
      } else {
        return false;
      }
    }
    
    return !!name && name.trim() !== '';
  }).sort((a: any, b: any) => {
    const { name: nameA, location: locationA } = extractFields(a);
    const { name: nameB, location: locationB } = extractFields(b);
    
    // Get provider names for sorting
    const getProviderName = (platform: any) => {
      const providerField = platform['Provider'];
      if (Array.isArray(providerField) && providerField.length > 0) {
        const providerId = typeof providerField[0] === 'string' ? providerField[0] : (providerField[0].id || providerField[0]);
        return providerLookup[providerId] || providerId;
      } else if (providerField) {
        const providerId = typeof providerField === 'string' ? providerField : (providerField.id || providerField);
        return providerLookup[providerId] || providerId;
      }
      return '';
    };
    
    const providerA = getProviderName(a);
    const providerB = getProviderName(b);
    
    switch (sortBy) {
      case 'name-asc':
        return nameA.localeCompare(nameB);
      case 'name-desc':
        return nameB.localeCompare(nameA);
      case 'provider-asc':
        return providerA.localeCompare(providerB);
      case 'provider-desc':
        return providerB.localeCompare(providerA);
      case 'location-asc':
        return (locationA || '').localeCompare(locationB || '');
      case 'location-desc':
        return (locationB || '').localeCompare(locationA || '');
      default:
        return nameA.localeCompare(nameB);
    }
  });

  return (
    <DirectoryProvider>
      {/* Top Section Background (matches homepage) */}
      <div className="absolute top-0 left-0 w-full h-[320px] bg-neutral-900 bg-gradient-to-b from-neutral-900 to-transparent z-0 pointer-events-none" />
      {/* Main Content */}
      <div className="relative z-10">
        {/* Hero Header */}
        <div className="w-full h-64 md:h-80 bg-gradient-to-r from-primary-500 to-accent-500 shadow-lg overflow-hidden relative rounded-none">
          <div className="max-w-7xl mx-auto w-full h-full flex flex-col md:flex-row md:items-center px-6">
            <div className="w-full">
              <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
                <span className="text-xl md:text-2xl lg:text-3xl font-medium flex items-center gap-2">
                  <img 
                    src="/LabAtlas-Logo-White-Full.png" 
                    alt="LabAtlas Logo" 
                    className="h-6 w-6 md:h-8 md:w-8 object-contain"
                  />
                  LabAtlas
                </span>
                Spatial Biology Platforms
              </h1>
              <p className="text-xl md:text-2xl text-white/90 leading-relaxed">
                Explore and compare spatial biology platforms to discover the right technology for your research workflow.
              </p>
            </div>
          </div>
        </div>
        
        {/* Main Content */}
        <div className="max-w-7xl mx-auto px-6 py-8 min-h-screen">
          {/* Breadcrumb */}
          <div className="mb-6">
            <Breadcrumb 
              items={[
                { label: 'Platforms', isCurrentPage: true }
              ]} 
            />
          </div>
          
          {/* Filter Bar */}
          <PlatformFilterBar
            providerOptions={providerOptions}
            technologyOptions={technologyOptions}
            samplesOptions={samplesOptions}
            readoutOptions={readoutOptions}
            plexTypeOptions={plexTypeOptions}
            chemistryOptions={chemistryOptions}
            resolutionOptions={resolutionOptions}
            throughputOptions={throughputOptions}
            slideResolutionOptions={slideResolutionOptions}
            applicationOptions={applicationOptions}
            selectedProviders={selectedProviders}
            setSelectedProviders={setSelectedProviders}
            selectedTechnologies={selectedTechnologies}
            setSelectedTechnologies={setSelectedTechnologies}
            selectedSamples={selectedSamples}
            setSelectedSamples={setSelectedSamples}
            selectedReadouts={selectedReadouts}
            setSelectedReadouts={setSelectedReadouts}
            selectedPlexTypes={selectedPlexTypes}
            setSelectedPlexTypes={setSelectedPlexTypes}
            selectedChemistry={selectedChemistry}
            setSelectedChemistry={setSelectedChemistry}
            selectedResolution={selectedResolution}
            setSelectedResolution={setSelectedResolution}
            selectedThroughput={selectedThroughput}
            setSelectedThroughput={setSelectedThroughput}
            selectedSlideResolution={selectedSlideResolution}
            setSelectedSlideResolution={setSelectedSlideResolution}
            selectedApplication={selectedApplication}
            setSelectedApplication={setSelectedApplication}
            techLoading={false} // Changed to false as technologyOptions is now local
            providerLoading={providerLoading}
          />
          {/* Active Filter Pills */}
          <PlatformActiveFilters
            selectedCountries={selectedCountries}
            setSelectedCountries={setSelectedCountries}
            selectedProviders={selectedProviders}
            setSelectedProviders={setSelectedProviders}
            selectedTechnologies={selectedTechnologies}
            setSelectedTechnologies={setSelectedTechnologies}
            selectedSamples={selectedSamples}
            setSelectedSamples={setSelectedSamples}
            selectedProducts={selectedProducts}
            setSelectedProducts={setSelectedProducts}
            selectedReadouts={selectedReadouts}
            setSelectedReadouts={setSelectedReadouts}
            selectedPlexTypes={selectedPlexTypes}
            setSelectedPlexTypes={setSelectedPlexTypes}
            selectedChemistry={selectedChemistry}
            setSelectedChemistry={setSelectedChemistry}
            selectedResolution={selectedResolution}
            setSelectedResolution={setSelectedResolution}
            selectedThroughput={selectedThroughput}
            setSelectedThroughput={setSelectedThroughput}
            selectedSlideResolution={selectedSlideResolution}
            setSelectedSlideResolution={setSelectedSlideResolution}
            selectedApplication={selectedApplication}
            setSelectedApplication={setSelectedApplication}
            technologyOptions={technologyOptions}
            productOptions={productOptions}
            onClearFilters={() => {
              setSelectedCountries([]);
              setSelectedProviders([]);
              setSelectedTechnologies([]);
              setSelectedSamples([]);
              setSelectedProducts([]);
              setSelectedReadouts([]);
              setSelectedPlexTypes([]);
              setSelectedChemistry([]);
              setSelectedResolution([]);
              setSelectedThroughput([]);
              setSelectedSlideResolution([]);
              setSelectedApplication([]);
            }}
            viewMode={viewMode}
            setViewMode={setViewMode}
            sortBy={sortBy}
            setSortBy={setSortBy}
          />
          
          {/* Platforms Display */}
          <PlatformDirectorySection
            filteredPlatforms={filteredAndSortedPlatforms}
            viewMode={viewMode}
            extractFields={extractFields}
            technologyOptions={technologyOptions}
            productOptions={productOptions}
            technologyLookup={technologyLookup}
            selectedProviders={selectedProviders}
            selectedTechnologies={selectedTechnologies}
            selectedProducts={selectedProducts}
            selectedSamples={selectedSamples}
            selectedReadouts={selectedReadouts}
            selectedPlexTypes={selectedPlexTypes}
            selectedChemistry={selectedChemistry}
            selectedResolution={selectedResolution}
            selectedThroughput={selectedThroughput}
            selectedCountries={selectedCountries}
          />
        </div>
      </div>
      <footer className="w-full py-8 bg-neutral-900">
        <div className="max-w-7xl mx-auto px-4 text-center text-neutral-500 text-sm">
          © 2025 LabAtlas. All rights reserved.
    </div>
      </footer>
    </DirectoryProvider>
  );
}